import { useContext } from "react";
import { ArrowRightIcon } from "evergreen-ui";
import { flatMap } from "lodash";
import { ConfigureButtons, Link, Pane, Text } from "components/materials";
import { formatTrimDate } from "helpers/dateHelpers";
import { DRAW_STATE } from "helpers/enums";
import t from "helpers/translate";
import { majorScale, ThemeContext } from "helpers/utilities";
import { defaultViews as defaultDrawReportViews } from "components/templates/ReportsDrawsTable";
import { BlankSlate, BlankSlateType } from "./BlankSlate";
import { Table } from "./Table";
import { CardHeading } from "./CardHeading";
import { CardLinkButton } from "./CardLinkButton";

export const OUTSTANDING_DRAWS_CONFIGURATION_SETTINGS = {
  i: "outstandingDraws",
  x: 0,
  y: 3,
  w: 1,
  h: 1,
  disabled: false,
};

export function OutstandingDraws({
  cards,
  isConfigurable,
  isDisabled,
  name,
  organization,
  projects,
  setCards,
}) {
  const relevantDraws = flatMap(
    projects,
    ({ id: projectId, name: projectName, draws }) =>
      draws.map(({ id: drawId, name: drawName, ...draw }) => ({
        ...draw,
        drawId,
        drawName,
        projectId,
        projectName,
      }))
  ).filter((draw) => draw.state !== DRAW_STATE.FUNDED && draw.processingData);

  const rows = relevantDraws.map((draw) => {
    const {
      projectId,
      projectName,
      drawId,
      drawName,
      processingData: { daysToProcess: elapsed, processingStartDate },
    } = draw;

    const submissionDate = formatTrimDate(processingStartDate);
    const elapsedText = `${elapsed} ${elapsed === 1 ? "day" : "days"}`;

    return {
      drawId,
      drawName,
      elapsed,
      elapsedText,
      projectId,
      projectName,
      submissionDate,
    };
  });

  const showBlankSlate = rows.length === 0;

  const columns = [
    {
      name: "Project",
      renderCell: (row) => <Project row={row} />,
      sortKey: "projectName",
      width: "30%",
    },
    {
      name: "Draw",
      renderCell: (row) => <Draw row={row} />,
      sortKey: "drawName",
      width: "25%",
    },
    {
      name: "Submission Date",
      sortKey: "submissionDate",
      valueKey: "submissionDate",
      width: "30%",
    },
    {
      name: "Elapsed",
      sortKey: "elapsed",
      valueKey: "elapsedText",
      width: "15%",
    },
  ];

  const cardLinkConfig =
    defaultDrawReportViews.find((view) => view.name === "Outstanding Draws")
      ?.config || "";

  const cardLinkHref = `/reports/draws?referrerSelectedOrgId=${organization.id}&table=${cardLinkConfig}`;

  return (
    <Pane
      alignItems="space-between"
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      padding={majorScale(2)}
    >
      <Pane
        display="flex"
        justifyContent="space-between"
        paddingBottom={majorScale(2)}
      >
        <CardHeading disabled={isDisabled} text="Outstanding Draws" />
        {isConfigurable && (
          <ConfigureButtons
            isDisabled={isDisabled}
            cards={cards}
            setCards={setCards}
            name={name}
          />
        )}
        {!isConfigurable && <CardLinkButton href={cardLinkHref} />}
      </Pane>
      {showBlankSlate ? (
        <BlankSlate type={BlankSlateType.Draws} />
      ) : (
        <Pane
          alignItems="center"
          display="flex"
          flex="1"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Table
            cellBorderY={false}
            columns={columns}
            defaultSortDirection="desc"
            defaultSortKey="elapsed"
            hasFooter={false}
            maxHeight="250px"
            rows={rows}
            width="100%"
          />
          <Text fontStyle="italic" maxWidth="55%">
            {t("portfolioInsights.outstandingDraws.note")}
          </Text>
        </Pane>
      )}
    </Pane>
  );
}

function Project({ row }) {
  const theme = useContext(ThemeContext);

  return (
    <Pane cursor="pointer" is={Link} to={`/projects/${row.projectId}`}>
      <Pane alignItems="center" display="flex" justifyContent="space-between">
        <Text color={theme.colors.linkBlue} size={400}>
          {row.projectName}
        </Text>
        <Pane>
          <ArrowRightIcon color={theme.colors.linkBlue} />
        </Pane>
      </Pane>
    </Pane>
  );
}

function Draw({ row }) {
  const theme = useContext(ThemeContext);

  return (
    <Pane is={Link} to={`/projects/${row.projectId}/draws/${row.drawId}`}>
      <Pane alignItems="center" display="flex" justifyContent="space-between">
        <Text color={theme.colors.linkBlue} size={400}>
          {row.drawName}
        </Text>
        <Pane>
          <ArrowRightIcon color={theme.colors.linkBlue} />
        </Pane>
      </Pane>
    </Pane>
  );
}
